import { Injectable } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import { firstValueFrom, forkJoin, from, Observable, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { LocalComponentStore } from '../store/local-component.store';
import { OrderService } from './order.service';
import { ModalService } from './modal.service';
import { OrderItem } from '../models/order-item.model';
import { Order } from '../models/order.model';
import { AuthService } from './auth.service';
import { AlertType } from '../enums/alert-type';
import { Address } from '../models/address.model';
import { Product } from '../models/product.model';
import { AddressService } from './address.service';
import { BudgetService } from './budget.service';
import { OrderItemDelivery } from '../models/order-item-delivery.model';
import {
    checkIOCodeAgainstOrder,
    computeAvailableAddresses,
    isPaymentMethodDefined,
    setupDistributionListDeliveries,
    validateOrderItem,
    validateWbsForUnique,
} from '../shared/helpers';
import { ProductService } from './product.service';
import { isEmptyArray } from '../shared/utils';
import { Budget } from '../models/budget.model';
import { CREDIT_CARD_PAYMENT_ERROR_CODE } from '../constants/error.codes';
import { PaymentService } from './payments.service';
import { MobileCheckServiceService } from './mobile-check-service.service';
import { Router } from '@angular/router';
import { OrderWindow } from '../models/order-window.model';
import { ECOMMERCE_TYPE, PREORDER_TYPE } from '../constants/order-types';
import { EntityService } from './entity.service';
import { RecommendedProductsService } from './recommended-products.service';
import { ConfigService } from './config.service';
import { MultiTenantService } from './multi-tenant.service';
import { EUDRService } from './eudr.service';
import {PnpBudgetService} from "./pnp-budget.service";

interface OrderItemState {
    updating: boolean;
    updatingOrderForProductId: number;
}

export interface OrderItemEudrState {
    hasRestriction: boolean;
    restrictedDeliveryUuids: ReadonlySet<string>;
    errorMessage: string | null;
    footerMessage: string | null;
}

@Injectable()
export class OrderItemService {

    private componentStore = new LocalComponentStore<OrderItemState>({
        updating: false,
        updatingOrderForProductId: 0,
    });


    /** Synchronous accessors preserve the previous public observable fields. */
    get updating(): boolean { return this.componentStore.snapshot(s => s.updating); }
    set updating(v: boolean) { this.componentStore.patchState({ updating: v }); }

    get updatingOrderForProductId(): number { return this.componentStore.snapshot(s => s.updatingOrderForProductId); }
    set updatingOrderForProductId(v: number) { this.componentStore.patchState({ updatingOrderForProductId: v }); }

    //  if any order item of the order is changed then this will be set to order id, otherwise 0.
    //  This  will be check in safe guard to show confirmation message
    public orderWithIdHasChanges = 0;

    constructor(private orderService: OrderService,
                private productService: ProductService,
                private modalService: ModalService,
                private authService: AuthService,
                private addressService: AddressService,
                private budgetService: BudgetService,
                private toastr: ToastrService,
                private paymentService: PaymentService,
                private mobileCheck: MobileCheckServiceService,
                private router: Router,
                private entityService: EntityService,
                private recommendedService: RecommendedProductsService,
                private configService: ConfigService,
                private multiTenantService: MultiTenantService,
                private eudrService: EUDRService,
                private pnpBudgetService: PnpBudgetService,
    ) {
    }

    private checkPaymentDefined(order: Order, showOrderSettings = false): Observable<Order> {
        if (!order || !order.id || order.limited) {
            return of(null);
        }


        if (order.isNotReal) {

            return this.modalService.createOrderWithSettings({orderType: order.type}).pipe(
                tap((retOrder: Order) => {
                    if (retOrder) {
                        // remove fake order
                        this.orderService.updateOrderList(-1);
                    }
                })
            )
        }


        // check if order has cost center
        if (!isPaymentMethodDefined(order, this.authService.user, this.authService.canShowSAP && this.configService.showBillingInfo)) {

            return from(this.modalService.showSAPModal(order, showOrderSettings, true)).pipe(map(retOrder => {
                // check order payment after order settings update
                if (isPaymentMethodDefined(retOrder, this.authService.user,
                    this.authService.canShowSAP && this.configService.showBillingInfo)) {
                    return retOrder;
                }
                return null;
            }));

        } else {
            return of(order);
        }
    }

    public checkWbsForDeliveries(order: Order, orderItem: OrderItem): string {
        if ( this.multiTenantService.canShowWBS) {
            if (!order.isNotReal && !order.isWBSDefined && !order.isInternalOrderDefined) {
                // check if some delivery doesn't have wbs
                const hasWbs = orderItem.deliveries.some( d => !!d.internalOrder);
                if (!hasWbs) {
                    return 'Please define WBS or Internal Order in the order settings.';
                }
            }
        }
        return '';
    }

    public saveOrderItem(order: Order, orderItem: OrderItem, originalOrderItem: OrderItem = null):
        Observable<{ purchaseResult: { added: boolean, updated: boolean, deleted: boolean, hasError: boolean}
                    order: Order,
                    orderItem: OrderItem,
                    budgets?: Budget[]}> {

        if (!order || !orderItem || !this.authService.canEditOrder(order)) {
            return of(null);
        }

        orderItem.calculateQuantity();
        const copiedItem = new OrderItem(originalOrderItem ? originalOrderItem : orderItem);

        const purchaseResult = {added: false, updated: false, deleted: false, hasError: false};

        // validate diageo - if  wbs code is defined
        const wbsValidationError = this.checkWbsForDeliveries(order, orderItem);
        if (wbsValidationError) {
            this.toastr.warning(wbsValidationError);
            return of (null);
        }

        const errorMsg  = this.getOrderItemValidationError(orderItem)
        if (errorMsg) {
            this.toastr.warning(errorMsg);
            return of(null);
        }

        return this.checkPaymentDefined(order).pipe(
            switchMap(result => {
                order = result;
                if (!order) {
                    return of(null);
                }


                this.updating = true;

                // orderItem = this.budgetService.computeBudgetAmount(orderItem);

                if (orderItem.id) {

                    if (orderItem.quantity === 0) {
                        const product = new Product(orderItem.product);
                        // delete item
                        this.updatingOrderForProductId = orderItem.product_id;
                        // this branch already forces its own product refetch below, so skip
                        // deleteOrderItem's internal cache refresh to avoid a redundant round-trip
                        return this.orderService.deleteOrderItem(order, orderItem, true).pipe(
                            switchMap(retOrder => {
                                if (!retOrder) {
                                    // delete failed - restore item
                                    purchaseResult.hasError = true;
                                    return of({purchaseResult, orderItem: copiedItem, order});
                                }
                                purchaseResult.deleted = true;
                                const wslrEntityId = retOrder.isWSLR ? retOrder.entity_id : 0;

                                return this.productService.getProduct(product.id, wslrEntityId, false, true).pipe( map( retProduct => {
                                    // setup new order item
                                    this.getProductPurchasedDetails(product.id);
                                    const newOrderItem = this.setupOrderItem(order, retProduct || product);
                                    this.applyDistributionListDeliveries(order, newOrderItem);
                                    this.toastr.success('Your item has been deleted.');
                                    return {purchaseResult, orderItem: newOrderItem, order};
                                }));
                            })
                        );
                    } else {
                        // update item
                        const orderItemId = orderItem.id;
                        const budgetId = orderItem.financials_total?.budget_id;
                        this.updatingOrderForProductId = orderItem.product_id;
                        // Capture which address IDs had deliveries before save.
                        // Explicitly-removed ones won't be here; zero-qty ones will be.
                        const preSaveAddressIds = new Set(
                            orderItem.deliveries.map(d => d.uniqueAddressId).filter(Boolean)
                        );
                        return this.orderService.updateOrderItem(order, orderItem).pipe(
                            map(retOrder => {
                                if (retOrder) {
                                    purchaseResult.updated = true;
                                    const updatedOrderItem = retOrder.items.find(i => i.id === orderItemId);
                                    updatedOrderItem.changed = false;
                                    updatedOrderItem.financials_total.budget_id = budgetId;
                                    // Recreate empty deliveries for all current preset/DL addresses,
                                    // then filter to only those that existed pre-save, which restores
                                    // zero-qty rows without recreating explicitly-removed ones.
                                    this.applyDistributionListDeliveries(order, updatedOrderItem);
                                    if (preSaveAddressIds.size > 0) {
                                        updatedOrderItem.deliveries = updatedOrderItem.deliveries.filter(
                                            d => !d.uniqueAddressId || preSaveAddressIds.has(d.uniqueAddressId)
                                        );
                                    }
                                    this.productService.populatedUpdatedProduct(updatedOrderItem.product);
                                    this.getProductPurchasedDetails(updatedOrderItem?.product?.id)
                                    const isOW = !!retOrder?.isByingWindow;
                                    const isWide = !!retOrder?.budgets?.length;
                                    this.showItemToast('updated', isOW, isWide);
                                    return {purchaseResult, orderItem: updatedOrderItem, order};
                                } else {
                                    purchaseResult.hasError = true;
                                    return {purchaseResult, orderItem: copiedItem, order};
                                }
                            })
                        );
                    }

                } else {
                    this.updatingOrderForProductId = orderItem.product?.id || orderItem.product_id;

                    checkIOCodeAgainstOrder(orderItem, order);

                    // Capture which address IDs exist before save so explicitly-removed addresses
                    // are not recreated by setupDistributionListDeliveries after the server responds.
                    const preSaveAddressIds = new Set(
                        orderItem.deliveries.map(d => d.uniqueAddressId).filter(Boolean)
                    );

                    return this.orderService.createOrderItem(order, orderItem).pipe(
                        map(retOrderItem => {
                            if (retOrderItem) {
                                retOrderItem.changed = false;
                                purchaseResult.added = true;
                                this.productService.populatedUpdatedProduct(retOrderItem?.product);
                                this.productService.productAddedToCart.next(retOrderItem?.product);
                                this.getProductPurchasedDetails(retOrderItem?.product?.id);
                                this.applyDistributionListDeliveries(order, retOrderItem);
                                if (preSaveAddressIds.size > 0) {
                                    retOrderItem.deliveries = retOrderItem.deliveries.filter(
                                        d => !d.uniqueAddressId || preSaveAddressIds.has(d.uniqueAddressId)
                                    );
                                }
                                const isOW = !!order?.isByingWindow;
                                this.showItemToast('added', isOW);
                                // set order currency
                                if (!order.currency) {
                                    const currency = retOrderItem.product.currency;
                                    order.currency =  currency;
                                }
                            } else {
                                purchaseResult.hasError = true;
                            }
                            return {purchaseResult, orderItem: retOrderItem, order};
                        })
                    );
                }
            }),
            tap( (retVal) => {
                // after  any order update - reset order budgets  that user  should recalc them manually
                if (retVal && retVal?.order?.id > 0) {
                    this.pnpBudgetService.resetOrderBudgets(retVal.order);
                }
            }),
            tap((retVal) => {
                if (!retVal || !retVal.purchaseResult) {
                    return;
                }

                if (!retVal.purchaseResult.added && !retVal.purchaseResult.updated && !retVal.purchaseResult.deleted) {
                    return;
                }

                this.productService.getInventoryAllocationForProduct(retVal?.orderItem?.product);
            }),
            switchMap( retVal => {

                if (!retVal || !retVal?.order?.id) {
                    return of(retVal);
                }

                // refetch  the order to get all changes related to budgets and order totals
                return this.orderService.refetchOrderFinancials(retVal?.order).pipe(
                  map( retOrder => {
                    if (!retOrder) {
                        return retVal;
                    }
                    retVal.order = retOrder;
                    return retVal;
                }));
            }),
           //  temporary  hide budgets  fetching  as it causes too much delay
          //  - will be refactored in future to fetch only affected budgets instead of fetching for whole order item
          //   switchMap( retVal => {
          //       if (!retVal) {
          //           return of(retVal);
          //       }
          //       return this.budgetService.fetchBudgetItemForOrderItem(retVal?.orderItem).pipe( map(retOrderItem => {
          //           retVal.orderItem = retOrderItem;
          //           retVal.budgets = this.budgetService.getBudgetsForOrderItem(retVal?.orderItem);
          //           return retVal;
          //       }));
          //   }),
          //   tap(  result => {
          //       if (result.orderItem.budgets && result.orderItem.budgets.length) {
          //           for (const budget of result.orderItem.budgets) {
          //               this.toastr.info(`
          //                   <div class="row flex">
          //                       <div class="col-12">
          //                           <span class="font-weight-bold font-3">${budget.name}</span>
          //                       </div>
          //                   </div>
          //                   <div class="row mt-1 flex-nowrap">
          //                       <div class="col-6">
          //                           <span>BUDGET:</span>
          //                           <span class="price font-weight-bold font-2">${budget.currency?.symbol ?? '$'}${formatTwoDecimals(budget.budget_total)}</span>
          //                       </div>
          //                       <div class="col-6">
          //                           <span>YTD:</span>
          //                           <span class="price font-weight-bold font-2">${budget.currency?.symbol ?? '$'}${formatTwoDecimals(budget.budget_ytd)}</span>
          //                       </div>
          //                   </div>
          //                   <div class="row mt-1 flex-nowrap">
          //                       <div  class="col-6">
          //                           <span>CART TOTAL</span>
          //                           <span class="price font-weight-bold font-2">${budget.currency?.symbol ?? '$'}${formatTwoDecimals(budget.budget_used)}</span>
          //                       </div>
          //                       <div  class="col-6">
          //                           <span class="desktop-only">BUDGET REMAINING</span>
          //                           <span class="mobile-only">REMAINING</span>
          //                           <span class="price font-weight-bold font-2 d-flex">${budget.currency?.symbol ?? '$'}${formatTwoDecimals(budget.budget_remaining)}</span>
          //                       </div>
          //                   </div>`
          //                 , undefined, { enableHtml: true, toastClass: 'ngx-toastr toast-wide'});
          //           }
          //       }
          //
          //   }),
            tap( () => {
                this.updating = false;
                this.updatingOrderForProductId = 0;
                this.orderWithIdHasChanges = 0;
            })
        );


    }

    private showItemToast(
        action: 'added' | 'updated',
        isOrderWindow: boolean,
        useWideToaster: boolean = false
    ): void {
        const base =
            action === 'added'  ? 'Your item has been added.'
                : action === 'updated' ? 'Your item has been updated.'
                    : '';

        // the stop belongs to the sentence above, so the buy window note only adds the space
        const suffix = isOrderWindow
            ? ' Your selections will be submitted automatically at the end of the Buy Window.'
            : '';
        
        let toasterClass = 'ngx-toastr';
        if (useWideToaster) {
            toasterClass = toasterClass.concat(' toast-wide');
        }

        this.toastr.success(`${base}${suffix}`, undefined,  {toastClass: toasterClass});
    }


    public getOrderItemFor(order: Order, orderItem: OrderItem, product: Product): OrderItem {
        if (orderItem) {
            return orderItem;
        }
        return this.setupOrderItem(order, product);
    }


    public editOrderItemModal(order: Order, orderItem: OrderItem, showOrderSelector = false): Promise<OrderItem | null> {

        if (this.mobileCheck.mobile) {
            // redirect to product page
            const product = orderItem.product;
            let url = product.productURL;
            if (product.isBuyingWindow && product.window_id !== order.window_id) {
                url = product.getProductUrlForOrderWindow(order.window_id)
            }
            return this.router.navigate([url]).then( () => orderItem);
        }

        // Always clone from the authoritative item in the order, not the reference handed in.
        // A failed save leaves the inline row holding a dirty (unsaved) order item, but order.items[x]
        // is never mutated by the modal. Cloning from there guarantees the modal reopens in the last
        // valid state instead of reusing an invalid in-flight selection (e.g. allocation group).
        const sourceOrderItem =
            (orderItem.id ? order.items?.find(i => i.id === orderItem.id) : null)
            ?? order.findOrderItemByProduct(orderItem.product)
            ?? orderItem;
        let edittedOrderItem: OrderItem = new OrderItem(sourceOrderItem);

        const checkForChanges = (updatedOrderItem) => {

            if (!this.authService.canEditOrder(order)) {
                return  true;
            }

            if (!updatedOrderItem.changed) {
                return true;
            }

            if (this.hasFailedTransactions) {
                return true;
            }

            const noShippingMethods = updatedOrderItem.deliveries.some((delivery) => delivery.method === '');
            if (noShippingMethods) {
                return true;
            }

            const internalOrderValidationError = validateWbsForUnique(order, updatedOrderItem, this.configService.wbsValidationOptions);
            if (internalOrderValidationError) {
                this.toastr.warning(internalOrderValidationError, 'Warning');
                return;
            }

            return this.showSaveConfirmation().then((result) => {
                if (!result) {
                    return true;
                }

                const itemFromOrder = order.items?.find(x => x);

                if (itemFromOrder && itemFromOrder.product.currency.code !== updatedOrderItem.product.currency.code) {
                    this.toastr.warning('This item is not in the same currency. Please create a new cart');
                    return true;
                }

                return firstValueFrom(this.saveOrderItem(order, updatedOrderItem).pipe(map((updateResut) => {
                    edittedOrderItem = updateResut.orderItem ? updateResut.orderItem : null;
                    return true;
                })), { defaultValue: true })
            });

        };

        return this.modalService.editOrderItemModal(order, edittedOrderItem, checkForChanges, showOrderSelector).then((resolvedOrderItem) => {
            if (resolvedOrderItem) {
                return resolvedOrderItem;
            }
            return edittedOrderItem.changed ? edittedOrderItem : null;
        }).catch(() => {
            return null;
        });
    }

    public showSaveConfirmation(options: {actionLabel: string, cancelLabel: string} = {actionLabel: 'Save',
        cancelLabel: 'Skip & Continue'}): Promise<boolean> {
        return this.modalService.showAlert({
            type: AlertType.Confirm,
            class: 'modal-sm',
            headerClass: 'state',
            title: 'Save changes',
            message: 'Your have made changes to your order that are not saved.',
            actionLabel: options.actionLabel,
            cancelLabel: options.cancelLabel
        });
    }


    /** Test seam over the `validateOrderItem` helper - see the note on `applyDistributionListDeliveries`. */
    protected getOrderItemValidationError(orderItem: OrderItem): string {
        return validateOrderItem(orderItem);
    }

    /**
     * Indirection over the `setupDistributionListDeliveries` helper purely so specs can
     * substitute it. The Angular unit-test builder bundles each spec together with the
     * source it imports, so an imported module binding can no longer be replaced with
     * `vi.spyOn(helpers, ...)` the way it could under Jest's CommonJS transform.
     */
    protected applyDistributionListDeliveries(order: Order, orderItem: OrderItem): void {
        setupDistributionListDeliveries(order, orderItem);
    }

    public setupOrderItem(order: Order, product: Product): OrderItem {

        if (!order || !product) {
            return null;
        }

        const userRoles = {canOrder: true, isEmployee: this.authService.isEmployee};
        const orderItem = order.getItemForProduct(product, {
            defaultDelivery: null,
            userRole: userRoles,
        });

        this.applyDistributionListDeliveries(order, orderItem);


        if (this.canOrder(order)) {

            // Select first address
            if (orderItem.deliveries.length > 0 && !orderItem.deliveries[0].street_1) {
                if (order.isWSLR) {
                    // set default WLS address
                    const entityAddress = Address.fromEntityLocation(order.entity.getDefaultShippingLocation());
                    if (entityAddress) {
                        orderItem.deliveries[0].setAddress(entityAddress, this.authService.user, userRoles);
                    }
                    // this.setInitialDeliveryAddress(this._orderItem.deliveries[0], entityAddress);
                } else  {
                    // set user's default address
                    const availableAddresses = computeAvailableAddresses(this.addressService.addresses, order);
                    if (!isEmptyArray(availableAddresses)) {

                        // find default address
                        const defaultAddress  =  availableAddresses.find(a => a.isDefault);
                        if (defaultAddress) {
                            orderItem.deliveries[0].setAddress(defaultAddress, this.authService.user, userRoles);
                        }
                    }
                }
            }
            this.findBudget(orderItem);
        }

        return orderItem;
    }

    private findBudget(orderItem: OrderItem) {
        const budgetId = orderItem?.financials_total?.budget_id;
        if (budgetId === -1) {
            orderItem.budgetItem = this.budgetService.emptyBudget;
        } else if (orderItem.financials_total.budget_id > 0) {
            orderItem.budgetItem = this.budgetService.budgets.find( b => b.id === orderItem.financials_total.budget_id);
        } else {
            const hardLimitBudget = this.budgetService.getHardlimitBudgetForProduct(orderItem.product);
            if (hardLimitBudget) {
                orderItem.financials_total.budget_id = hardLimitBudget.id;
                orderItem.budgetItem = hardLimitBudget;
            }
        }
    }

    addAnotherAddress(orderItem: OrderItem, order: Order): OrderItem {
        if (!orderItem || !this.canOrder(order)) {
            return orderItem;
        }

        const availableAddresses = computeAvailableAddresses(this.addressService.addresses, order);

        if (isEmptyArray(availableAddresses)) {
            return orderItem;
        }

        const firstAvailableAddress = availableAddresses.find(a => a.isDefault) || availableAddresses[0];
        const shippingMethod = orderItem.product.findFirstAvailableShippingMethod(firstAvailableAddress.country_code);

        // check if this address has pendingDelete = true otherwise create a new one
        const newDelivery = new OrderItemDelivery({
                type: 'SHIPPING',
                quantity: 0,
                data: {},
                method: shippingMethod
            });
        newDelivery.setAddress(firstAvailableAddress, this.authService.user,
            {canOrder: true, isEmployee: this.authService.isEmployee});

        orderItem.updateDelivery(newDelivery);

        return new OrderItem(orderItem);
    }

    // Update EUDR state for the whole order item based on its deliveries
    public updateEudrStateForWholeItem(orderItem: OrderItem): void {
        const restrictedAddress = orderItem.deliveries
            .map(d => d.getAddress())
            .find(address =>
                address && this.eudrService.checkEudrForAddress(orderItem.product, address)
            );

        if (restrictedAddress) {
            this.eudrService.checkEudrRestrictions(orderItem.product, restrictedAddress);
        }
    }

    /**
     * Computes all delivery-level EUDR flags in one pass without publishing shared service state.
     * Checkout uses this result for its read-only rows, avoiding two whole-item rescans from every
     * delivery component while keeping the editable product/cart flows unchanged.
     */
    public evaluateEudrStateForWholeItem(orderItem: OrderItem): OrderItemEudrState {
        const restrictedDeliveryUuids = new Set<string>();
        let errorMessage: string | null = null;
        let footerMessage: string | null = null;

        if (!orderItem?.product || !orderItem.deliveries?.length) {
            return {hasRestriction: false, restrictedDeliveryUuids, errorMessage, footerMessage};
        }

        for (const delivery of orderItem.deliveries) {
            const address = delivery.getAddress();
            if (!address) {
                continue;
            }

            const result = delivery.hasEudrRestriction(orderItem.product, address);
            if (!result.hasRestriction) {
                continue;
            }

            restrictedDeliveryUuids.add(delivery.uuid);
            errorMessage ??= result.errorMessage ?? null;
            footerMessage ??= result.footerMessage ?? null;
        }

        return {
            hasRestriction: restrictedDeliveryUuids.size > 0,
            restrictedDeliveryUuids,
            errorMessage,
            footerMessage
        };
    }

    // Check if any delivery in the order item is EUDR restricted
    public isEudrRestricted(orderItem: OrderItem): boolean {
        if (!orderItem?.deliveries?.length) { return false; }

        return orderItem.deliveries.some(delivery => {
            const address = delivery.getAddress();
            return address && this.eudrService.checkEudrForAddress(orderItem.product, address);
        });
    }

    private updateBudget(orderItem: OrderItem): Observable<Budget[]> {
        return this.budgetService.updateBudget(orderItem);
    }

    public calculateDecisionPoints(order: Order, orderItem: OrderItem, quantity): Observable<OrderItem> {

        if (!this.canOrder(order)) {
            return  of(orderItem);
        }

        return this.productService.calculateDecisionPoints(order.attr_decision_point_id, orderItem.product.id, quantity).pipe(
            map( result => {

                if (isEmptyArray(result)) {
                    return orderItem;
                }

                result.forEach( i => {
                   const address = new Address(i.address);

                   let delivery = orderItem.findDelivery(address);
                   if (delivery) {
                       delivery.quantity = i.quantity;
                   } else {
                       const shippingMethod = orderItem.product.findFirstAvailableShippingMethod(address.country_code);
                       delivery = new OrderItemDelivery({
                           quantity: i.quantity,
                           type: 'SHIPPING',
                           method: shippingMethod,
                           is_uneligible: i.is_uneligible});
                       delivery.setAddress(address, this.authService.user,
                           {canOrder: true, isEmployee: this.authService.isEmployee});
                   }

                   orderItem.updateDelivery(delivery);
                });
                return orderItem;
            })
        );
    }

    public checkout(order: Order): Observable<Order> {
        if (!order) {
            return of(order);
        }
        return this.orderService.checkout(order).pipe( map( retOrder => {
            if (retOrder) {
                if (retOrder.error?.errorCode === CREDIT_CARD_PAYMENT_ERROR_CODE) {
                    const transactionId = retOrder.error.data.transactionId;
                    this.modalService.showCreditCardPaymentFailed(order, transactionId);
                }
            }
            return  retOrder;

        }))
    }

    public get hasFailedTransactions(): boolean {
        return this.paymentService.hasFailedTransactions;
    }


    public canOrder(order: Order): boolean {
        return  this.authService.canEditOrder(order);
    }

    public hasAccessToProduct(order: Order, product: Product): Observable<boolean> {
        if (!order || !order.loaded || !product) {
            return of(false);
        }

        // check only for wslr order
        if (!order.isWSLR) {
            return of(true);
        }

        return of(true);
        // return this.productService.getProduct(product.id, order.entity_id, false, true).pipe(
        //     map( retOrder => {
        //         return !!retOrder;
        //     })
        // );
    }

    private getOrCreateOrder(orderId, orderType, canCreateOrder): Observable<Order> {
        if (orderId) {
            return this.orderService.getOrder(orderId);
        }

        if (canCreateOrder) {
            return this.modalService.createOrderWithSettings({orderType});
        }
        return of(null);
    }

    public shopProduct(order: Order, product: Product, canCreateOrder = false, showOrderSelector = false) {
        if (!order || !product) {
            return;
        }

        this.productService.getProduct(product.id, order.entity_id, false, true).pipe(
            switchMap( retProduct => {
                if (!retProduct) {
                    return of({shopOrder: null, shopProduct: null});
                }
                return this.getOrCreateOrder(order.id,  order.type, canCreateOrder).pipe( map( retOrder => {
                    return {shopOrder: retOrder, shopProduct: retProduct};
                }));
            }),
            switchMap(({shopOrder, shopProduct}) => {
                order = shopOrder;
                if (!shopOrder || !shopOrder?.id || !shopProduct) {
                    return of ({shopOrder: null, shopProduct: null, hasAccess: false});
                }
                return this.hasAccessToProduct(order, shopProduct).pipe( map( result => ({shopOrder, shopProduct, hasAccess: result})));
            }),
            switchMap(({shopOrder, shopProduct, hasAccess}) => {
                if (hasAccess && shopProduct && shopOrder) {
                    this.productService.populateProduct(shopProduct);
                    const orderItem = this.getOrderItemFor(shopOrder, null, shopProduct);
                    const retOrderItem = this.getOrderItemFor(shopOrder, orderItem, shopProduct);
                    return from(this.editOrderItemModal(order, retOrderItem, showOrderSelector))
                } else {
                    return of(null);
                }
            })
        ).subscribe();
    }


    public showProductInModal(product: Product, order?: Order, showOrderSelector = false): Observable<unknown> {
         if (!product) {
             return of (null);
         }

         let showingProduct: Product = product;
         return this.getActualProduct(product).pipe(
             tap( retProduct => showingProduct = retProduct),
             switchMap( () => this.validateProduct(showingProduct)),
             switchMap( valid => {

                 let clonedOrderItem = this.getClonedOrderItem(showingProduct, order, valid);
                 if (!valid) {
                     // if product cannot be purchasable - show as readonly modal
                    return this.showModalWindow(clonedOrderItem, order, true);
                 }

                 return this.getActiveOrder(showingProduct).pipe(
                     switchMap( retOrder => {
                         if (retOrder && retOrder.containsProduct(showingProduct)) {
                             clonedOrderItem = this.getClonedOrderItem(showingProduct, retOrder, true);
                         }
                         return this.showModalWindow(clonedOrderItem, retOrder, false, showOrderSelector);
                     })
                 );
             }),
         );

    }

    public prefetchCapexForEntity(orderItem: OrderItem) {
        if (!orderItem || !orderItem.product) {
            return;
        }

        // prefetch only for employees
        if (!this.authService.isEmployee) {
            return;
        }

        const {product} = orderItem;
        if (product.hasLease) {
            const entityIds: number[] =
                orderItem.deliveries.filter( delivery =>
                    (delivery.data.entity_id > 0 && delivery.data.entity_id !== this.authService.user.entity_id))
                    .map( delivery => delivery.data.entity_id);

           if (isEmptyArray(entityIds)) {
               return;
           }
            forkJoin( entityIds.map( id => this.entityService.prefetchCapex(id))).subscribe()
        }
    }

    public checkLeaseStatus(): Observable<string> {
        return this.entityService.reloadCapex(this.authService.user.entity_id);
    }

    public shouldShowLeaseWarning(product: Product) {
        if (!product.hasLease) {
            return false;
        }

        if (this.authService.isEmployee) {
            return false;
        }

        return product.attr_capex_type !== this.authService.user.entity.capex;
    }


    public isProductRecommended(product: Product, order: Order = null): boolean {
        if (!product) {
            return null;
        }
        if (this.authService.isWholeSaler) {
            return product.is_recommended;
        }

        if (this.authService.isEmployee) {
            return product.is_recommended && (order?.isWSLR || order?.isDecisionPoint)
        }

    }

    public canShowRecommendsProducts(order: Order): boolean {
        if (!order) {
            return false;
        }
        if (order.isByingWindow) {
            if (this.authService.isWholeSaler) {
                return true;
            }

            if (this.authService.isEmployee) {
                return order.isWSLR || order.isDecisionPoint;
            }
        }

        return false;
    }

    public showRecommendedProductExportResult(exportType: 'pdf' | 'xlsx') {
        this.modalService.showRecommendedProductExportResult(exportType);
    }


    private getClonedOrderItem(product: Product, order: Order, withReset = false): OrderItem {
        const originalOrderItem = order.getItemForProduct(product, {});
        const clonedOrderItem = new OrderItem(originalOrderItem);
        if (withReset) {
            clonedOrderItem.resetFinancialTotals();
            delete clonedOrderItem.id;
            clonedOrderItem.deliveries.forEach( d => {
                delete d.id;
                if (d.chili_output) {
                  delete d.chili_output;
                }
                if (d.chili_output_thumbnail) {
                  delete d.chili_output_thumbnail;
                }

                if (d.chili_project_id) {
                  delete d.chili_project_id;
                }
            });
            clonedOrderItem.checkForAvailableItems();
            // Strip cancelled/shipped state so a reorder of a cancelled item opens as a
            // fresh, purchasable line instead of a cancelled one (AB reorder-cancelled fix).
            clonedOrderItem.prepareForReorder();
        }

        return clonedOrderItem
    }

    // check if there is a product replacement
    private getActualProduct(product: Product): Observable<Product> {
        if (!product.replacedProductId) {
            return of(product);
        }

        return this.productService.getProduct(product.replacedProductId);
    }

    // validate product with order window. For OD order window is 0
    private validateProduct(product: Product): Observable<boolean> {
        if (!product) {
            return of(null);
        }

        // check if product canbe reordered
        if (!product.productActive) {
            return of(false);
        }

        let orderWindow: OrderWindow = null;
        if (product.isBuyingWindow) {
            if (!this.orderService.activeOrderWindow) {
                return of(null);
            }
            orderWindow = this.orderService.activeOrderWindow;
        }

        // check if product is still can buy
        return this.productService.validateProduct(product, orderWindow);
    }


    private getOrderByType(type: string): Observable<Order> {
        const order = type === ECOMMERCE_TYPE ? this.orderService.activeODOrder : this.orderService.activeBWOrder;
        if (order) {
            if (!order.loaded || order.isLazy) {
                return this.orderService.getOrder(order.id);
            }
            return of(order);
        }
        return this.modalService.createOrderWithSettings({orderType: type});
    }

    private getActiveOrder(product: Product): Observable<Order> {
        if (!product) {
            return of(null);
        }
        const orderType = product.isOnDemand ? ECOMMERCE_TYPE : PREORDER_TYPE;
        return this.getOrderByType(orderType).pipe(
            switchMap( retOrder => {
                if (!this.authService.canEditOrder(retOrder)) {
                    return of(null);
                }
                return this.checkPaymentDefined(retOrder, true);
            }
        ));
    }

    private showModalWindow(orderItem: OrderItem, order: Order, readonly = false, showOrderSelector = false) {
        if (!orderItem || !order) {
            return of(null);
        }

        if (readonly) {
            return from(this.modalService.showReadonlyProductModal(order, orderItem))
        }

        return from(this.editOrderItemModal(order, orderItem, showOrderSelector));
    }

    private getProductPurchasedDetails(productId: number) {
        this.recommendedService.getProductOrdersIfo(productId);
    }
}
